import graphqlRequest from "Services/assignment/assignmentRequest";
import {formatLocale_LowerCaseHyphen} from "Services/locale";
import {addAssignmentForStudent} from "Services/assignment/addAssignment/addAssignment";
import {filterEligibleAssignments, orderAssignmentsByEntityType} from "Services/assignment/functions";
import GET_ASSIGNMENTS, {CHECK_FOR_SCHOOL_WINDOWS} from "Services/assignment/getAssignment/getAssignmentGraphql";
import GET_DISTRICT_ASSIGNMENTS from "./getDistrictAssignmentGraphql";
import {ENTITY_TYPE, ASSIGNMENT_STATUS, GRADES} from "Services/assignment/constants";
import { inCurrentScreeningWindow,getScreeningWindowsForSchoolYear } from "../../screeningWindowService";
import { ASSIGNMENT_TYPE } from "../constants";
import { getAssignmentTemplate } from "../getAssignmentTemplate";
import { cleanManifest, transformManifest } from "../manifest";
import {updateBatchAssignments} from "Services/assignment/updateBatchAssignments";
import { logSessionInfo } from "../../logger";
import { skipActivityInManifest } from "../updateManifest";
import { SPANISHCONFIG } from "../../../constants/constants";
import { getUserData } from "../../API";

/**
 * fetch assignments for an entity
 * @param entityId {string} - entity id (student, classroom, school, or district id) to fetch assignments for
 * @param entityType {string} - entity type associated with the given entity id
 * @returns {Promise<array<assignment>>}
 */
export async function getAssignments(entityId, entityType = ENTITY_TYPE.STUDENT, grade=0, school,district){
  const defaultEntities = [
    { entityType: "SCHOOL", entityId: school},
    { entityType: "DISTRICT", entityId: district},
    { entityType: "CLASS", entityId: 1234}  //Placeholder to ensure optimized assignment call
  ];

  const response = await graphqlRequest(GET_ASSIGNMENTS, {
    entityId,
    entityType,
    defaultEntities
  });
  const {assignment} = response;

  //If there are screening window assignments, grab the current template for school and grade
  let assignments = await Promise.all(assignment.map(async (a)=>{
    return (entityType === ENTITY_TYPE.STUDENT &&
            a.status === ASSIGNMENT_STATUS.ASSIGNED &&
            (a.entityType === ENTITY_TYPE.DISTRICT || a.entityType === ENTITY_TYPE.SCHOOL) &&
            school) ? await transformScreeningWindowAssignment(a,grade,school,defaultEntities,a.entityType) : Promise.resolve(a);
  }));

  return assignments;
}

export async function getDistrictAssignments(entityId){
  let dateFrom = new Date().toISOString().split('T')[0];
  let inputs = {
    entityId,
    entityType: ENTITY_TYPE.DISTRICT,
    filter: {
      dateFrom,
      status: [ASSIGNMENT_STATUS.ASSIGNED]
    }
  };

  const response = await graphqlRequest(GET_DISTRICT_ASSIGNMENTS, inputs);
  const {assignment} = response;
  return assignment;
}

export async function getSchoolAssignments(entityId){
  let dateFrom = new Date().toISOString().split('T')[0];
  let inputs = {
    entityId,
    entityType: ENTITY_TYPE.SCHOOL,
    filter: {
      dateFrom,
      status: [ASSIGNMENT_STATUS.ASSIGNED]
    }
  };

  const response = await graphqlRequest(GET_DISTRICT_ASSIGNMENTS, inputs);
  const {assignment} = response;
  return assignment;
}

/**
 * fetch assignments for a student entity
 * @param studentId {string}
 * @param isBilingual {boolean} - student's isESL setting
 * @returns {Promise<array<assignment>>}
 */
export async function getAssignmentForStudent(studentId, session) {
  let {isESL,actualGrade,schoolId,districtId,screenerEnabled,assessmentEnabled} = session;

  //Skip everything if all types are disabled
  if(screenerEnabled === false && assessmentEnabled === false) return null;

  const allAssignments = await getAssignments(studentId, ENTITY_TYPE.STUDENT, actualGrade,schoolId,districtId);
  logSessionInfo("all_assignments",{allAssignments});
  const filteredAssignments = removeCompletedDistrictAssignments(allAssignments,actualGrade,screenerEnabled,assessmentEnabled);
  const pendingAssignments = filterPendingAssignments(filteredAssignments);
  const eligibleAssignments = filterEligibleAssignments(pendingAssignments);

  let nextAssignment = orderAssignmentsByEntityType(eligibleAssignments)[0];
  if(nextAssignment){
    nextAssignment = await checkEnabledTasks(nextAssignment,session);
    if(nextAssignment.entityType !== ENTITY_TYPE.STUDENT) {
      nextAssignment = await addAssignmentForStudent(nextAssignment, studentId, isESL);
    }
    await unassignDuplicateStudentAssignments(nextAssignment, eligibleAssignments);
  }
  logSessionInfo("next_assignment",{nextAssignment});
  return transformAssignment(nextAssignment);
}

export function removeCompletedDistrictAssignments(assignments,grade,screenerEnabled,assessmentEnabled){
  let completedBenchmark = assignments.find((studentAssignment)=>completedBenchmarkInCurrentScreeningWindow(studentAssignment,screenerEnabled,assessmentEnabled));
  let schoolAssignment = assignments.find(assignment=>assignment.entityType === ENTITY_TYPE.SCHOOL && assignment.status === ASSIGNMENT_STATUS.ASSIGNED);

  return assignments
  .filter((assignment)=>{
    //Keep all student assignments
    if(assignment.entityType === ENTITY_TYPE.STUDENT){
      return true;
    }

    if(!isWindowEnabledByGrade(grade)) return false;

    //Filter out all school assignments if a student has completed a benchmark
    if(assignment.entityType === ENTITY_TYPE.SCHOOL){
      if(completedBenchmark) return false;
      return assignment.status === ASSIGNMENT_STATUS.ASSIGNED;
    }

    //Filter out all district assignments if a student has completed a benchmark or if there is a school assignment
    if(assignment.entityType === ENTITY_TYPE.DISTRICT){
      if(completedBenchmark || schoolAssignment) return false;
      return assignment.status === ASSIGNMENT_STATUS.ASSIGNED;
    }
  });
}

export function isWindowEnabledByGrade(grade){
  const {windowDisabledByGrade} = getUserData();
  return (!windowDisabledByGrade || windowDisabledByGrade && !windowDisabledByGrade[grade])
}

/**
 * filters to array of assignments with ASSIGNED status
 * @param assignments
 * @returns {array<assignment>}
 */
export function filterPendingAssignments(assignments){
  return assignments.filter(({status}) => [ASSIGNMENT_STATUS.ASSIGNED, ASSIGNMENT_STATUS.IN_PROGRESS].includes(status));
}

/**
 * transforms an assignment to the model expected by the web app
 * @param assignment
 * @returns {object<assignment>}
 */
export function transformAssignment(assignment){
  return assignment ? {
    ...assignment,
    status: assignment.status ? assignment.status : ASSIGNMENT_STATUS.ASSIGNED,
    manifest: assignment.manifest.map(item => {
      return {
        ...item,
        activity: item.activity ? item.activity : [],
        locale: formatLocale_LowerCaseHyphen(item.locale),
        status: item.status ? item.status : ASSIGNMENT_STATUS.ASSIGNED,
        type: item.activityType,
      }
    })
  } : null;
}

function completedBenchmarkInCurrentScreeningWindow(assignment,screenerEnabled,assessmentEnabled){
  if(assignment && (assignment.entityType !== ENTITY_TYPE.STUDENT ||
                    assignment.assignmentType !== ASSIGNMENT_TYPE.BENCHMARK ||
                    !([ASSIGNMENT_STATUS.DONE,ASSIGNMENT_STATUS.COMPLETE].includes(assignment.status)))) return false;
  let benchmarkAssignment = assignment.manifest.find(task=>(task.activityType === "assessment" || (task.activityType === "dyslexia" && !assessmentEnabled)));
  let results = benchmarkAssignment && benchmarkAssignment.activity.reduce((inWindow,activity)=>{
     return inCurrentScreeningWindow(new Date(activity.createdAt)) ? true : inWindow;
  },false);
  results = results && benchmarkAssignment && [ASSIGNMENT_STATUS.DONE,ASSIGNMENT_STATUS.COMPLETE].includes(benchmarkAssignment.status);
  return results;
}

// We do two things in this method:
// 1. Configure the manifest based on the school's settings.
// 2. Override district windows if a school has set one.
async function transformScreeningWindowAssignment(assignment,grade,school,defaultEntities,entityType){
  let requests = [getAssignmentTemplate({entityId: school, entityType: ENTITY_TYPE.SCHOOL, grade: GRADES[grade], defaultEntities})];
  requests.push(getSchoolAssignmentInThisWindow(school));
  let responses = await Promise.all(requests);
  let schoolTemplate = responses[0];
  let schoolAssignment = responses[1];

  // Put the school's manifest (assignment settings) into the district assignment
  assignment.manifest = schoolTemplate.manifest;
  // If there is a school screening window set, override the district's. Even if it is in the past or future.
  if(entityType === ENTITY_TYPE.DISTRICT && schoolAssignment && schoolAssignment.assignment && schoolAssignment.assignment.length){
    assignment.status = ASSIGNMENT_STATUS.UNASSIGNED;
  }
  return assignment;
}

async function getSchoolAssignmentInThisWindow(school){
  let window = getScreeningWindowsForSchoolYear().find((win)=>win.isCurrent);
  let dateTo = window.endDate.toISOString().split('T')[0];
  let dateFrom = window.startDate.toISOString().split('T')[0];
  return graphqlRequest(CHECK_FOR_SCHOOL_WINDOWS,{entityId: school, dateFrom, dateTo});
}

async function checkEnabledTasks(assignment,session){
  if(assignment.assignmentType !== ASSIGNMENT_TYPE.BENCHMARK) return assignment;
  let {screenerEnabled, assessmentEnabled, isESL, spanishConfig, assessmentLanguageOverride} = session;
  let manifest = JSON.parse(JSON.stringify(assignment.manifest));

  // Use assessmentLanguageOverride if it's not null or undefined, otherwise use spanishConfig
  let languageConfig = assessmentLanguageOverride !== null && assessmentLanguageOverride !== undefined ? assessmentLanguageOverride : spanishConfig;

  // Make sure to honor the SPANISH and ENGLISH_ONLY configurations. Assignment service has to return both locales because it doesn't know about a
  // student's bilingual status. This block will filter out the wrong locale if a student-assignment hasn't been created yet.
  if((assignment.entityType !== ENTITY_TYPE.STUDENT) && isESL && [SPANISHCONFIG.englishOnly,SPANISHCONFIG.spanishOnly].includes(languageConfig)){
    let locales = languageConfig === SPANISHCONFIG.englishOnly ? ["EN_US"] : ["ES_MX"];
    manifest = transformManifest(manifest,locales,false);
  }
  let cleanedManifest = cleanManifest(manifest);

  let skippedActivities = []
  assignment.manifest = cleanedManifest.filter((task)=>{
    let isEnabled = (task.activityType === 'assessment' && assessmentEnabled) || (task.activityType !== 'assessment' && screenerEnabled);
    // This block allows an assignment to self-heal in English-only and Spanish-only configs if it got created with the wrong settings
    // from a screening window.
    if(isESL && [SPANISHCONFIG.englishOnly,SPANISHCONFIG.spanishOnly].includes(languageConfig) && assignment.parentId){
      let enabledLocale = languageConfig === SPANISHCONFIG.englishOnly ? "EN_US" : "ES_MX";
      if(task.locale !== enabledLocale){
        isEnabled = false;
      }
    }
    if(!isEnabled){
      skippedActivities.push(task);
    }
    return isEnabled;
  });

  if(skippedActivities.length){
    await Promise.all(skippedActivities.map(async(task)=>{
      await skipActivityInManifest(assignment.assignmentId,task.activityType,task.locale)
    }));
  }

  return assignment;
}

async function unassignDuplicateStudentAssignments(nextAssignment, assignments){
  const duplicateAssignments = assignments.filter(({entityType, assignmentId, assignmentType, status}) =>
    entityType === ENTITY_TYPE.STUDENT
    && assignmentId !== nextAssignment.assignmentId
    && assignmentType === nextAssignment.assignmentType
    && [ASSIGNMENT_STATUS.ASSIGNED,ASSIGNMENT_STATUS.IN_PROGRESS].includes(status));

  const batchInput = duplicateAssignments.map((assignment)=>{
    return {
      assignmentId: assignment.assignmentId,
      entityId: assignment.entityId,
      entityType: assignment.entityType,
      status: ASSIGNMENT_STATUS.UNASSIGNED,
    }
  });

  return await updateBatchAssignments(batchInput);
}
